import type { ReportTemplate } from '../types';

interface Section {
  name?: string;
  fixed_prefix?: string;
  normal_default?: string;
  slot_name?: string;
  conditional?: boolean;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Render a template's `template_config.sections` into report HTML matching the
 * editor's conventions (<h2> study title, <h3> section headings, <p> bodies).
 *
 * This is the radiologist's structured starting point: selecting a template
 * drops its canonical normals into the editor as an *input to the flow*, so the
 * radiologist (and the AI) work from a known-good scaffold rather than a blank
 * page — which reduces the chance of missed/incorrect sections.
 */
export function templateToHtml(template: ReportTemplate | null | undefined): string {
  const cfg = template?.template_config as { sections?: Section[] } | undefined;
  const sections = cfg?.sections || [];
  if (!sections.length) return '';

  const parts: string[] = [];
  for (const s of sections) {
    const name = (s.name || '').trim();
    const body = [s.fixed_prefix, s.normal_default]
      .map((x) => (x || '').trim())
      .filter(Boolean)
      .join(' ')
      .trim();

    // Title section: no slot, no content — just the study heading.
    const isTitle = !s.slot_name && !s.fixed_prefix && !s.normal_default;
    if (isTitle) {
      if (name) parts.push(`<h2>${escapeHtml(name.toUpperCase())}</h2>`);
      continue;
    }

    parts.push(`<h3>${escapeHtml(name)}</h3>`);
    // Empty body (e.g. Clinical History) still gets a paragraph so the section
    // is visible and ready for dictation.
    parts.push(`<p>${escapeHtml(body)}</p>`);
  }
  return parts.join('');
}
